import { useTranslation as useReactI18nextTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";

// Custom hook that provides typed translation functions
// Adds an optional "show keys" mode which returns the raw translation key
// instead of the translated value. The mode can be enabled at build time via
// REACT_APP_I18N_SHOW_KEYS=true or toggled at runtime (persisted in localStorage).
export const useTranslation = (namespace?: string) => {
  const { t, i18n, ready } = useReactI18nextTranslation(namespace);

  // Build-time env to force showing keys (useful for screenshots / QA)
  // Vite exposes env vars via import.meta.env. Use VITE_I18N_SHOW_KEYS=true to enable.

  const initialShowKeys = false;

  const [showKeys, setShowKeysState] = useState<boolean>(!!initialShowKeys);

  useEffect(() => {
    // Persist user preference in localStorage when available
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          "i18n:showKeys",
          showKeys ? "true" : "false"
        );
      } catch {
        // ignore storage errors (e.g., privacy mode)
      }
    }
  }, [showKeys]);

  const setShowKeys = useCallback((v: boolean) => setShowKeysState(!!v), []);

  interface TranslationParameters {
    [key: string]: string | number | boolean | null | undefined;
  }

  type WrapFunction = (key: string, parameters?: TranslationParameters) => string;

  const wrap = useCallback(
    (prefix: string): WrapFunction =>
      (key: string, parameters?: TranslationParameters) => {
        const fullKey = `${prefix}${key}`;
        return showKeys ? fullKey : t(fullKey, parameters);
      },
    [showKeys, t]
  );

  return {
    t,
    i18n,
    ready,
    // expose the mode and a setter so the UI can toggle it at runtime
    showKeys,
    setShowKeys,
    // Helper functions for common translations (they honor showKeys)
    tNav: wrap("navigation."),
    tCommon: wrap("common."),
    tFilters: wrap("filters."),
    tStrategy: wrap("strategy."),
    tChart: wrap("chart."),
    tTable: wrap("table."),
    tQuickFilters: wrap("quickFilters."),
    tPortfolio: wrap("portfolio."),
    tUI: wrap("ui."),
    tDrawdowns: wrap("drawdowns."),
    tOverview: wrap("overview."),
    tDetail: wrap("detail."),
    tMimicking: wrap("factorMimicking."),
  };
};

export default useTranslation;
